import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useTextMessaging, useUserSession } from '../../../../models/hooks/appStateHooks';
import { CoffeeAppShell } from '../../../components/AppShell';
import { useIsCoffeeNavCompact } from '../../../components/AppShell/hooks/useIsCompactNav';
import { SingleEmailComposerModal } from '../../../components/email/SingleEmailComposerModal';
import { WebFullscreenModal } from '../../../components/fullscreen/WebFullscreenModal';
import { PrivateRoute } from '../../../components/PrivateRoute';
import { ROUTES } from '../../../constants/routes';
import { Automations } from '../../../containers/Automations';
import { BuildInfo } from '../../../containers/BuildInfo';
import { Company } from '../../../containers/Company';
import { Contact } from '../../../containers/Contact';
import { EditAutomationTemplate } from '../../../containers/EditAutomationTemplate';
import { LazyLoader } from '../../../containers/LazyLoader';
import { Logout } from '../../../containers/Logout';
import { ScheduleMeetingSettingsPage } from '../../../containers/ScheduleMeeting/ScheduleMeetingSettingsPage';
import { ManageUsers } from '../../../containers/Settings/ManageUsers';
import { Contacts } from '../../../pages/Contacts';
import { CoffeeDashboard } from '../CoffeeDashboard';
import { CoffeeMyTemplates } from '../CoffeeMyTemplates';
import { CoffeeSettings } from '../Settings';
import { styleSheet } from './styles';
import { Redirect, Switch, useHistory, useParams } from 'react-router-dom';
import {
	EditAutomationTemplateStepsContext,
	IEditAutomationTemplateStepsContext,
} from '../../../../models/Automations';

const LazyReportingRoute = React.lazy(
	() => import(/* webpackChunkName: "reporting" */ '../../../containers/reporting')
);

const Page = ({ children }: { children: React.ReactNode }) => {
	const showingCompactNav = useIsCoffeeNavCompact();
	const routeContainerClassName = showingCompactNav
		? css(styleSheet.contentContainerChildCompact)
		: css(styleSheet.contentContainerChild);

	return (
		<CoffeeAppShell>
			{React.Children.map(children, child => {
				return React.cloneElement(child as React.ReactElement<any>, { routeContainerClassName }, null);
			})}
		</CoffeeAppShell>
	);
};

const AutomationsWithContext = () => {
	const params = useParams<{ accountId: string; id: string }>();
	const userSession = useUserSession();
	const history = useHistory();

	const automationTemplateId = params.id;
	const impersonationContext = userSession.impersonationContext;
	const [unsavedStep, setUnsavedStep] = React.useState<Api.IAutomationStep>(null);

	const editAutomationTemplateStepsContext = React.useMemo<IEditAutomationTemplateStepsContext>(() => {
		return {
			automationTemplateId,
			impersonationContext,
			setUnsavedStep,
			unsavedStep,
		};
	}, [automationTemplateId, impersonationContext, unsavedStep]);

	if (!automationTemplateId) {
		return null;
	}

	return (
		<EditAutomationTemplateStepsContext.Provider value={editAutomationTemplateStepsContext}>
			<EditAutomationTemplate
				onAutomationDeleted={() => history.replace('/automations')}
				onAutomationPublished={() => history.push('/automations')}
				onAutomationDisabled={() => history.push('/automations')}
			/>
		</EditAutomationTemplateStepsContext.Provider>
	);
};

export function DecafMainContainer() {
	const userSession = useUserSession();
	const textMessaging = useTextMessaging();
	const isCoffeeEnabled = userSession?.account?.features?.aida?.enabled;
	return (
		<>
			<Switch>
				{isCoffeeEnabled ? (
					<Redirect from='/' to='/dashboard/' exact={true} />
				) : (
					<Redirect from='/' to='/settings/' exact={true} />
				)}
				<PrivateRoute path='/logout' userSession={userSession}>
					<Logout userSession={userSession} textMessaging={textMessaging} />
				</PrivateRoute>
				<PrivateRoute path='/build-info' userSession={userSession}>
					<Page>
						<BuildInfo />
					</Page>
				</PrivateRoute>
				<PrivateRoute path='/dashboard/' userSession={userSession}>
					<Page>
						<CoffeeDashboard />
					</Page>
				</PrivateRoute>
				<PrivateRoute path='/templates' userSession={userSession}>
					<Page>
						<CoffeeMyTemplates />
					</Page>
				</PrivateRoute>
				<PrivateRoute path={`${ROUTES.contacts.people}/:id`} userSession={userSession} pageViewLogName='contact/view'>
					<Page>
						<Contact />
					</Page>
				</PrivateRoute>
				<PrivateRoute
					path={`${ROUTES.contacts.companies}/:id`}
					userSession={userSession}
					pageViewLogName='company/view'
				>
					<Page>
						<Company />
					</Page>
				</PrivateRoute>
				<Redirect from='/people' to={ROUTES.contacts.people} />
				<Redirect from='/companies' to={ROUTES.contacts.companies} />
				<PrivateRoute path={ROUTES.contacts.main} pageViewLogName='people' userSession={userSession}>
					<Page>
						<Contacts />
					</Page>
				</PrivateRoute>
				<PrivateRoute path='/automations/:id' userSession={userSession}>
					<Page>
						<AutomationsWithContext />
					</Page>
				</PrivateRoute>
				<PrivateRoute path='/automations' userSession={userSession}>
					<Page>
						<Automations />
					</Page>
				</PrivateRoute>
				<PrivateRoute path='/settings/admin/manage-users' userSession={userSession}>
					<Page>
						<ManageUsers />
					</Page>
				</PrivateRoute>
				<PrivateRoute path='/settings/:category?/:action?' userSession={userSession}>
					<Page>
						<CoffeeSettings />
					</Page>
				</PrivateRoute>
				<PrivateRoute path='/schedule-meeting' userSession={userSession}>
					<Page>
						<ScheduleMeetingSettingsPage />
					</Page>
				</PrivateRoute>
				<PrivateRoute path='/reporting' userSession={userSession}>
					<LazyLoader>
						<Page>
							<LazyReportingRoute />
						</Page>
					</LazyLoader>
				</PrivateRoute>
			</Switch>
			<WebFullscreenModal />
			<SingleEmailComposerModal />
		</>
	);
}
